<script setup lang="ts">
// WTA-style public footer (doc 04 §9.1): a deep-aubergine band with a brand block
// (wordmark + tagline + socials), "get the app" badges, four columns of link lists,
// an official-partners strip, and a legal bottom bar. Links that resolve to a real
// route navigate; the rest raise a "coming soon" toast (same pattern as the nav).
import { useUiStore } from '@/stores/ui.store'

const ui = useUiStore()
const year = new Date().getFullYear()

function soon(label: string) {
  ui.toast(`${label} is coming soon.`, 'info')
}

interface FooterLink {
  title: string
  to?: string
  soon?: boolean
}
interface FooterColumn {
  heading: string
  links: FooterLink[]
}

const columns: FooterColumn[] = [
  {
    heading: 'Explore',
    links: [
      { title: 'Rankings', to: '/rankings' },
      { title: 'Players', to: '/players' },
      { title: 'Courts', to: '/courts' },
      { title: 'Clubs', to: '/clubs' },
      { title: 'News', to: '/news' },
    ],
  },
  {
    heading: 'Compete',
    links: [
      { title: 'Tournaments', to: '/tournaments' },
      { title: 'Live Scores', soon: true },
      { title: 'Schedule', soon: true },
      { title: 'Results', soon: true },
    ],
  },
  {
    heading: 'The Association',
    links: [
      { title: 'About CPA', soon: true },
      { title: 'Officials & Referees', soon: true },
      { title: 'Press Center', soon: true },
      { title: 'Contact', soon: true },
    ],
  },
  {
    heading: 'Get Involved',
    links: [
      { title: 'Become a member', to: '/register' },
      { title: 'Sign in', to: '/login' },
      { title: 'Book a court', to: '/courts' },
      { title: 'Find a club', to: '/clubs' },
    ],
  },
]

const socials = [
  { icon: 'mdi-facebook', label: 'Facebook' },
  { icon: 'mdi-instagram', label: 'Instagram' },
  { icon: 'mdi-youtube', label: 'YouTube' },
  { icon: 'mdi-twitter', label: 'X' },
]

const partners = ['Caraga LGU', 'Region XIII Sports', 'PH Pickleball', 'Wyred']
</script>

<template>
  <footer class="cpa-footer text-white">
    <v-container class="py-12">
      <!-- Brand row -->
      <div class="cpa-footer__top">
        <div class="cpa-footer__brand">
          <RouterLink to="/" class="cpa-footer__logo cpa-display">CPA</RouterLink>
          <p class="cpa-footer__tagline">
            The home of pickleball in Caraga — official rankings, players, and live
            competition across Region XIII.
          </p>
          <div class="cpa-footer__socials">
            <button
              v-for="s in socials"
              :key="s.label"
              type="button"
              class="cpa-footer__social"
              :aria-label="s.label"
              @click="soon(s.label)"
            >
              <v-icon :icon="s.icon" size="20" />
            </button>
          </div>
        </div>

        <div class="cpa-footer__app">
          <div class="cpa-eyebrow cpa-footer__app-label">Get the app</div>
          <div class="cpa-footer__badges">
            <button type="button" class="cpa-footer__badge" @click="soon('iOS app')">
              <v-icon icon="mdi-apple" size="22" />
              <span>
                <small>Download on the</small>
                <strong>App Store</strong>
              </span>
            </button>
            <button type="button" class="cpa-footer__badge" @click="soon('Android app')">
              <v-icon icon="mdi-google-play" size="20" />
              <span>
                <small>Get it on</small>
                <strong>Google Play</strong>
              </span>
            </button>
          </div>
        </div>
      </div>

      <v-divider class="cpa-footer__rule" />

      <!-- Link columns -->
      <div class="cpa-footer__cols">
        <div v-for="col in columns" :key="col.heading" class="cpa-footer__col">
          <div class="cpa-eyebrow cpa-footer__heading">{{ col.heading }}</div>
          <ul class="cpa-footer__list">
            <li v-for="link in col.links" :key="link.title">
              <RouterLink v-if="link.to" :to="link.to" class="cpa-footer__link">
                {{ link.title }}
              </RouterLink>
              <button
                v-else
                type="button"
                class="cpa-footer__link cpa-footer__link--btn"
                @click="soon(link.title)"
              >
                {{ link.title }}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <v-divider class="cpa-footer__rule" />

      <!-- Partners strip -->
      <div class="cpa-footer__partners">
        <span class="cpa-eyebrow cpa-footer__partners-label">Official partners</span>
        <div class="cpa-footer__partner-row">
          <span v-for="p in partners" :key="p" class="cpa-footer__partner">{{ p }}</span>
        </div>
      </div>

      <!-- Legal bar -->
      <div class="cpa-footer__legal">
        <div class="cpa-footer__copy">
          © {{ year }} Caraga Pickleball Association · Region XIII — Caraga, Philippines
        </div>
        <div class="cpa-footer__legal-links">
          <button type="button" class="cpa-footer__legal-link" @click="soon('Privacy Policy')">
            Privacy
          </button>
          <button type="button" class="cpa-footer__legal-link" @click="soon('Terms of Use')">
            Terms
          </button>
          <button type="button" class="cpa-footer__legal-link" @click="soon('Cookie settings')">
            Cookies
          </button>
        </div>
      </div>

      <!-- Built-by credit -->
      <div class="cpa-footer__built">
        Built with <span class="cpa-footer__heart" aria-hidden="true">💗</span> by
        <a
          href="https://wyredinnovations.com/"
          target="_blank"
          rel="noopener noreferrer"
          class="cpa-footer__wyred"
        >
          Wyred Innovations
        </a>
      </div>
    </v-container>
  </footer>
</template>

<style scoped>
.cpa-footer {
  background: var(--cpa-purple-ink);
}

/* Brand row */
.cpa-footer__top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
}
.cpa-footer__brand {
  max-width: 420px;
}
.cpa-footer__logo {
  display: inline-block;
  font-size: 2.4rem;
  font-style: italic;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
  text-decoration: none;
  line-height: 1;
}
.cpa-footer__tagline {
  margin: 14px 0 18px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.95rem;
  line-height: 1.5;
}
.cpa-footer__socials {
  display: flex;
  gap: 10px;
}
.cpa-footer__social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.cpa-footer__social:hover {
  background: var(--cpa-green);
  color: var(--cpa-purple-ink);
  transform: translateY(-2px);
}

/* App badges */
.cpa-footer__app-label {
  color: var(--cpa-green-bright);
  margin-bottom: 12px;
}
.cpa-footer__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.cpa-footer__badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.cpa-footer__badge:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.45);
}
.cpa-footer__badge span {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}
.cpa-footer__badge small {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
}
.cpa-footer__badge strong {
  font-size: 0.95rem;
  font-weight: 700;
}

.cpa-footer__rule {
  border-color: rgba(255, 255, 255, 0.12);
  margin: 32px 0;
}

/* Link columns */
.cpa-footer__cols {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}
.cpa-footer__heading {
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 14px;
}
.cpa-footer__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.cpa-footer__link {
  display: inline-block;
  padding: 0;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.82);
  font-family: inherit;
  font-size: 0.95rem;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;
}
.cpa-footer__link:hover {
  color: var(--cpa-green-bright);
}

/* Partners */
.cpa-footer__partners {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 28px;
}
.cpa-footer__partners-label {
  color: rgba(255, 255, 255, 0.5);
}
.cpa-footer__partner-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.cpa-footer__partner {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
}

/* Legal */
.cpa-footer__legal {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 32px;
}
.cpa-footer__copy {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.82rem;
}
.cpa-footer__legal-links {
  display: flex;
  gap: 20px;
}
.cpa-footer__legal-link {
  border: none;
  background: none;
  padding: 0;
  color: rgba(255, 255, 255, 0.55);
  font-family: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  transition: color 0.15s ease;
}
.cpa-footer__legal-link:hover {
  color: #fff;
}

/* Built-by credit */
.cpa-footer__built {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  font-weight: 500;
}
.cpa-footer__heart {
  display: inline-block;
  transform: translateY(1px);
  animation: cpa-heart-beat 1.8s var(--cpa-ease) infinite;
}
.cpa-footer__wyred {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 700;
  text-decoration: none;
  transition: color var(--cpa-dur) var(--cpa-ease);
}
.cpa-footer__wyred:hover {
  color: var(--cpa-green-bright);
}
@keyframes cpa-heart-beat {
  0%, 100% { transform: translateY(1px) scale(1); }
  15% { transform: translateY(1px) scale(1.22); }
  30% { transform: translateY(1px) scale(1); }
}

@media (max-width: 720px) {
  .cpa-footer__cols {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 20px;
  }
}
</style>
